import {
  Popover,
  Select,
  Row,
  Col,
  Tag,
  Checkbox,
  Radio,
  message,
  Flex,
  Spin,
  Timeline,
  Popconfirm,
  Tooltip,
  Descriptions,
} from "antd";
import {
  EditAccess,
  CancelAccess,
  ApproveAccess,
  RejectAccess,
} from "../../../components/accessControl/AccessControl";
import { useAccessControl } from "../../../utils/useAccessControl";
import { FC, useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import dayjs from "dayjs";
import {
  Button,
  Alert,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { projectsApis } from "../../../api/projects";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import { useSelector } from "react-redux";
import FormCheckbox from "../../form-components/CheckboxComponent";
import { ReactTyped } from "react-typed";
import { Icon } from "../../../components/Component";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import Swal from "sweetalert2";
import debounce from "lodash.debounce";
import {
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { OwnerChangeReason } from "../phases/PhaseFieldDetailsUpdate";

const tagColors = [
  "#f28585", // red
  "#f2a679", // orange
  "#f2d479", // yellow
  "#a6f279", // green
  "#79f2f2", // teal
  "#79a6f2", // blue
  "#a679f2", // purple
  "#f279c4", // pink
];

// Get color by hash of label
export const getColorByLabel = (label: string) => {
  let sum = 0;
  for (let i = 0; i < label.length; i++) {
    sum += label.charCodeAt(i);
  }
  return tagColors[sum % tagColors.length];
};
export const tagRender = ({ label, closable, onClose }: CustomTagProps) => {
  const color = getColorByLabel((label || "")?.toString());

  return (
    <span className="custom-tag-wrapper">
      <Tag
        className="custom-tag"
        style={{ backgroundColor: color }}
        closable={closable}
        onClose={onClose}
      >
        {label}
      </Tag>
      <span className="custom-tag-tail" style={{ borderLeftColor: color }} />
    </span>
  );
};

const ProjectInformation: FC<{
  isViewMode: boolean;
  ProjectInDetailData: any;
  initialEditMode?: boolean;
  onBackToSummary?: () => void;
}> = ({
  isViewMode,
  ProjectInDetailData,
  initialEditMode = false,
  onBackToSummary,
}) => {
  const { id, derivedProjectStatus, extra } = ProjectInDetailData;
  const [messageApi, contextHolder] = message.useMessage();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { canEdit, canCancel, canApprove } = useAccessControl();

  const [ViewMode, setViewMode] = useState(isViewMode);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [isDirty, setIsDirty] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<any>(null);
  const formMethods = useForm({
    mode: "onChange",
  });
  const [tags, setTags] = useState<any[]>([]);
  const [deselectedTags, setDeselectedTags] = useState<any[]>([]);
  const {
    watch,
    handleSubmit,
    setValue,
    control,
    getValues,
    reset,
    formState: { isDirty: formIsDirty, dirtyFields },
  } = formMethods;
  const [sitesList, setSitesList] = useState<SelectOption[]>([]);
  const [ownersList, setOwnersList] = useState<SelectOption[]>([]);
  const [calendarList, setCalendarList] = useState<SelectOption[]>([]);
  const [ratecardsList, setRatecardsList] = useState<SelectOption[]>([]);
  const [groupsList, setGroupsList] = useState<SelectOption[]>([]);
  const [budgetList, setBudgetList] = useState<SelectOption[]>([]);
  const [boqList, setBoqList] = useState<SelectOption[]>([]);
  const [originalData, setOriginalData]: any = useState();
  const [resourceHeads, setResourceHeads] = useState<SelectOption[]>([]);
  const [ownerChangeModalOpen, setOwnerChangeModalOpen] = useState<{
    open: boolean;
    extraData: any;
  }>({
    open: false,
    extraData: {},
  });
  const [warning, setWarning] = useState<{
    error: any | undefined;
    message: string | undefined;
  }>({
    error: undefined,
    message: undefined,
  });
  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);
  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const lastFetchedProjectIdForTagsRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (id) {
      fetchDataById(id);
    }
  }, [id]);

  useEffect(() => {
    lastFetchedProjectIdForTagsRef.current = null;
  }, [id]);

  useEffect(() => {
    fetchRateCardDropDown();
    fetchProjectGroupDropDown();
    fetchProjectCalendarDropDown();
  }, []);

  const fetchSiteUsersBySiteId = async (siteId: string | number) => {
    try {
      const response = await projectsApis.fetchSiteUsers(siteId);
      const { data, status } = response || {};
      if (status) {
        setOwnersList(
          data?.map((item: any) => ({
            label: item.userName,
            value: item.appUserId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchRateCardDropDown = async () => {
    try {
      const response = await projectsApis.fetchRateCardDropDown();
      const { data, status } = response || {};
      if (status) {
        setRatecardsList(
          data?.map((item: any) => ({
            label: item.rateCardTitle,
            value: item.rateCardId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchProjectGroupDropDown = async () => {
    try {
      const response = await projectsApis.fetchProjectGroupDropDown();
      const { data, status } = response || {};
      if (status) {
        setGroupsList(
          data?.map((item: any) => ({
            label: item.projectGroupName,
            value: item.projectGroupId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchBudgetDropDown = async (siteId: any) => {
    try {
      const response = await projectsApis.fetchbudgetDropDown(siteId);
      const { data, status } = response || {};
      if (status) {
        const items = Array.isArray(data) ? data : data ? [data] : [];
        setBudgetList(
          items.map((item: any) => ({
            label: item.budgetName,
            value: item.headerId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchProjectCalendarDropDown = async () => {
    try {
      const response = await projectsApis.fetchProjectCalendarDropDown();
      const { data, status } = response || {};
      if (status) {
        setCalendarList(
          data?.map((item: any) => ({
            label: item.calendarTitle,
            value: item.calendarId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchMaterialBoqDropDown = async (siteId: any) => {
    try {
      const response = await projectsApis.fetchMaterialBoqDropDown(siteId);
      const { data, status } = response || {};
      if (status) {
        setBoqList(
          data?.map((item: any) => ({
            label: item.boqTitle,
            value: item.targetId,
          }))
        );
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility, id } = currentUser;
      setCurrentUserId(id);
      let sitesOptions = siteAccessibility?.map((item: any) => ({
        label: item.siteName,
        value: item.siteID,
      }));
      setSitesList(sitesOptions);
    }
  }, [currentUser]);

  const fetchDataById = async (id: any) => {
    try {
      // If a derivedProjectStatus is provided in state use it, otherwise fall back to extra.screenName
      const apiResponse: ApiResponse = await projectsApis.getProjectById(
        id,
        derivedProjectStatus || extra?.screenName
      );
      const { data, status } = apiResponse;
      if (status) {
        setOriginalData(data);
        fetchSiteUsersBySiteId(data.siteId);
        fetchMaterialBoqDropDown(data.siteId);
        fetchProjectTags();
        fetchBudgetDropDown(data.siteId);
        const {
          projectTitle,
          siteId,
          siteName,
          plannedStartDate,
          plannedEndDate,
          calendarId,
          projectDescription,
          budgetId,
          mustFinishDate,
          projectGroupId,
          isStrict,
          approvalRequired,
          autoApprovalRemarks,
          projectOwnerId,
          isPrivate,
          calendarTitle,
          projectRateCards,
          projectGroupName,
          projectUsers,
          budgetName,
          projectBoqs,
          projectTags,
          projectOwnerName,
        } = data;
        const siteNameFromList = sitesList.find(
          (s) => s.value === siteId
        )?.label;

        setValue("siteName", siteName || "-");
        setValue("projectTitle", projectTitle);
        setValue("siteId", siteId);
        setValue("calendarTitle", calendarTitle);
        setValue("projectRateCardName", projectRateCards?.[0]?.rateCardTitle ?? "");
        setValue("plannedStartDate", dayjs(new Date(plannedStartDate)));
        setValue("plannedEndDate", dayjs(new Date(plannedEndDate)));
        setValue("calendarId", calendarId);
        setValue("rateCardId", projectRateCards?.[0]?.rateCardId ?? null);
        setValue("projectDescription", projectDescription);
        setValue("projectGroupName", projectGroupName);
        setValue("budgetId", budgetId);
        setValue("budgetName", budgetName);
        setValue(
          "mustFinishDate",
          mustFinishDate && dayjs(new Date(mustFinishDate))
        );
        setValue("projectGroupId", projectGroupId);
        setValue("isStrict", isStrict);
        setValue("approvalRequired", approvalRequired == true ? "yes" : "no");
        setValue("autoApprovalRemarks", autoApprovalRemarks || "");
        setValue("projectOwnerId", projectOwnerId);
        setValue("projectOwnerName", projectOwnerName);
        if (projectUsers?.length > 0) {
          const resourceHeadIds = projectUsers
            .filter((user: any) => user.projectUserType === "Resource Head")
            .map((user: any) => user.userId);

          setValue("users", resourceHeadIds); // Pre-check checkboxes
        }
        setValue(
          "boqs",
          projectBoqs?.map((item: any) => item.boqTargetId)
        );
        setValue("isPrivate", isPrivate?.toString());
        setTags(
          projectTags?.map((tag: any) => ({
            label: tag.tagName,
            value: tag?.projectTagId,
          }))
        );
      } else {
        console.log(apiResponse);
      }
    } catch (error) {
      console.log("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setResourceHeads(
      ownersList?.filter((item) => item.value !== watch("projectOwnerId"))
    );
  }, [watch("projectOwnerId"), ownersList]);

  // Populate Tags dropdown options when projectTagsList loads
  useEffect(() => {
    if (projectTagsList?.length > 0) {
      fetchTagSuggestions("");
    }
  }, [projectTagsList]);

  useEffect(() => {
    setIsDirty(formIsDirty);
  }, [formIsDirty]);

  const getUsers = (data: any) => {
    const selectedUserIds = data.users || [];

    const originalProjectUsers = originalData.projectUsers || [];

    const originalResourceHeads = originalProjectUsers.filter(
      (user: any) => user.projectUserType === "Resource Head"
    );

    const originalUserIds = originalResourceHeads.map(
      (user: any) => user.userId
    );
    const newLines = selectedUserIds
      .filter((id: number) => !originalUserIds.includes(id))
      .map((id: number) => ({
        userId: id,
        projectUserType: "Resource Head", // Or dynamically decide the type
      }));

    // Removed
    const deletedLines = originalResourceHeads
      .filter((user: any) => !selectedUserIds.includes(user.userId))
      .map((user: any) => ({
        projectUserId: user.projectUserId,
      }));
    const payload = {
      newLines,
      deletedLines,
    };
    return payload;
  };

  const getUpdatedBoqs = (formValues: any) => {
    const selectedBoqIds: number[] = formValues.boqs || [];

    const originalBoqs = originalData?.projectBoqs || [];
    const originalBoqTargetIds = originalBoqs.map(
      (item: any) => item.boqTargetId
    );

    // Find newly added
    const newLines = selectedBoqIds
      .filter((id: number) => !originalBoqTargetIds.includes(id))
      .map((id: number) => ({
        boqTargetId: id,
      }));

    // Find removed
    const deletedLines = originalBoqs
      .filter((item: any) => !selectedBoqIds.includes(item.boqTargetId))
      .map((item: any) => ({
        projectBoqId: item.projectBoqId,
      }));

    return {
      newLines,
      deletedLines,
    };
  };

  const getUpdatedRateCards = (formValues: any) => {
    const selectedRateCardId = formValues.rateCardId;

    const originalRateCards = originalData?.projectRateCards || [];
    const originalRateCardId = originalRateCards[0]?.rateCardId;

    const newLines =
      selectedRateCardId && selectedRateCardId !== originalRateCardId
        ? [{ rateCardId: selectedRateCardId }]
        : [];

    const deletedLines =
      originalRateCardId && selectedRateCardId !== originalRateCardId
        ? [{ projectRateCardId: originalRateCards[0].projectRateCardId }]
        : [];

    return {
      newLines,
      deletedLines,
    };
  };

  const onSubmit = (data: any) => {
    const payload: any = {
      projectId: originalData.projectId, // Always required
      siteId: originalData.siteId, // Always required
      mode: "U", // Always required
    };

    if (dirtyFields.projectTitle) {
      payload.projectTitle = data.projectTitle;
    }
    if (dirtyFields?.projectOwnerId) {
      payload.projectOwnerId = data.projectOwnerId;
    }
    if (dirtyFields.projectDescription) {
      payload.projectDescription = data.projectDescription;
    }
    if (dirtyFields.plannedStartDate) {
      payload.plannedStartDate = dayjs(data.plannedStartDate).format(
        "YYYY-MM-DD"
      );
    }
    if (dirtyFields.plannedEndDate) {
      payload.plannedEndDate = dayjs(data.plannedEndDate).format("YYYY-MM-DD");
    }
    if (dirtyFields.mustFinishDate) {
      payload.mustFinishDate = dayjs(data.mustFinishDate).format("YYYY-MM-DD");
    }
    if (dirtyFields.calendarId) {
      payload.calendarId = data.calendarId;
    }

    if (dirtyFields.budgetId) {
      payload.budgetId = data.budgetId;
    }
    if (dirtyFields.projectGroupId) {
      payload.projectGroupId = data.projectGroupId;
    }
    if (dirtyFields.isStrict) {
      payload.isStrict = data.isStrict;
    }
    if (dirtyFields.approvalRequired) {
      payload.approvalRequired = data.approvalRequired;
    }
    if (data.approvalRequired === "no" && dirtyFields.autoApprovalRemarks) {
      payload.autoApprovalRemarks = data.autoApprovalRemarks;
    }
    // if(dirtyFields.projectOwnerId) {
    //   payload.projectOwnerId = data.projectOwnerId;
    // }
    if (dirtyFields.isPrivate) {
      payload.isPrivate = data.isPrivate;
    }
    if (dirtyFields.users) {
      const usersPayload = getUsers(data);
      if (
        usersPayload.newLines.length > 0 ||
        usersPayload.deletedLines.length > 0
      ) {
        payload.projectUsers = usersPayload;
      }
    }

    if (dirtyFields.boqs) {
      const boqsPayload = getUpdatedBoqs(data);
      if (
        boqsPayload.newLines.length > 0 ||
        boqsPayload.deletedLines.length > 0
      ) {
        payload.projectBoqs = boqsPayload;
      }
    }
    if (dirtyFields.rateCardId) {
      const rateCardPayload = getUpdatedRateCards(data);
      if (
        rateCardPayload.newLines.length > 0 ||
        rateCardPayload.deletedLines.length > 0
      ) {
        payload.projectRateCards = rateCardPayload;
      }
    }
    console.log("tags", tags, deselectedTags);
    if (tags || deselectedTags) {
      const newLines =
        tags
          ?.filter((t: any) => typeof t?.value === "string")
          ?.map((newTag: any) => ({
            tagName: newTag?.label,
          })) || [];

      const deletedLines =
        deselectedTags?.map((t: any) => ({
          projectTagId: t,
        })) || [];

      if (newLines.length > 0 || deletedLines.length > 0) {
        payload.projectTags = {
          ...(newLines.length > 0 && { newLines }),
          ...(deletedLines.length > 0 && { deletedLines }),
        };
      }
    }
    if (payload?.projectOwnerId) {
      setLoading(false);
      setOwnerChangeModalOpen({
        open: true,
        extraData: payload,
      });
    } else {
      updateProject(payload);
    }
  };

  const updateProject = async (payload: any) => {
    try {
      const response: ApiResponse = await projectsApis.updateProject(payload);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          text: data?.message,
          confirmButtonText: "Ok",
        }).then(() => {
          // Switch back to view mode after user clicks Ok in SweetAlert
          setIsEditMode(false);
          fetchDataById(id);
          // When the form was mounted directly into edit mode from the new
          // ProjectQuickViewCard summary, bounce the user back to that summary
          // so the post-edit view matches the initial view (same layout, fresh data).
          if (onBackToSummary) {
            onBackToSummary();
          }
        });
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const deactivateProject = async () => {
    try {
      const response: ApiResponse = await projectsApis.deactivateProject(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const activateProject = async () => {
    try {
      const response: ApiResponse = await projectsApis.activateProject(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const lockProject = async () => {
    try {
      const response: ApiResponse = await projectsApis.lockProject(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const unLockProject = async () => {
    try {
      const response: ApiResponse = await projectsApis.unLockProject(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const requestForApproval = async () => {
    try {
      const response: ApiResponse = await projectsApis.requestForApproval(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const archiveProject = async () => {
    setLoading(true);
    try {
      const response: ApiResponse = await projectsApis.archiveProject(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const closeProject = async () => {
    try {
      const response: ApiResponse = await projectsApis.closeProject(id);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarning({
          message,
          error,
        });
        setTimeout(() => {
          setWarning({
            message: "",
            error: "",
          });
        }, 10000);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const approveProject = async () => {
    Swal.fire({
      title: "Are you sure you want to approve?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response: ApiResponse = await projectsApis.approveProject(id);
          const { data, status, message, error } = response || {};
          if (status) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: data?.message,
              confirmButtonText: "Ok",
            });
            fetchDataById(id);
          } else {
            setWarning({
              message,
              error,
            });
            setTimeout(() => {
              setWarning({
                message: "",
                error: "",
              });
            }, 10000);
          }
        } catch (error) {
          console.log("error", error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const rejectProject = async () => {
    Swal.fire({
      title: "Are you sure you want to reject?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reject it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { value: reason } = await Swal.fire({
          title: "Enter Reason for Rejection",
          input: "text",
          inputPlaceholder: "Type your reason here",
          showCancelButton: true,
          confirmButtonText: "Submit",
          cancelButtonText: "Cancel",
          inputValidator: (value) => {
            if (!value) {
              return "You need to Enter Reason for rejection!";
            }
            return null;
          },
        });
        if (reason) {
          try {
            let reqObj = {
              projectId: id,
              reason: reason,
            };
            const response: ApiResponse = await projectsApis.rejectProject(
              reqObj
            );
            const { data, status, message, error } = response || {};
            if (status) {
              Swal.fire({
                icon: "success",
                title: "Success",
                text: data?.message,
                confirmButtonText: "Ok",
              });
              fetchDataById(id);
            } else {
              setWarning({
                message,
                error,
              });
              setTimeout(() => {
                setWarning({
                  message: "",
                  error: "",
                });
              }, 10000);
            }
          } catch (error) {
            console.log("error", error);
          } finally {
            setLoading(false);
          }
        }
      }
    });
  };

  const fetchProjectTags = async () => {
    if (!id) return;
    if (lastFetchedProjectIdForTagsRef.current === id) return;
    lastFetchedProjectIdForTagsRef.current = id;
    try {
      const response: any = await projectsApis.fetchProjectTags(id);
      const {
        data: tagsList,
        status,
      }: {
        status: boolean;
        data: { tagName: string; tagId: number | string }[];
      } = response || {};
      if (status && Array.isArray(tagsList)) {
        setProjectTagsList(tagsList);
      }
    } catch (error) {
      lastFetchedProjectIdForTagsRef.current = null;
      console.log(error);
    }
  };

  const fetchTagSuggestions = (search: string) => {
    const dataToSearch = projectTagsList || [];
    const filtered =
      search && search.length >= 1
        ? dataToSearch.filter((tag) =>
            tag?.tagName?.toLowerCase()?.includes(search?.toLowerCase())
          )
        : dataToSearch;

    const formatted = filtered.map((tag) => ({
      label: tag?.tagName,
      value: tag?.tagId,
    }));

    if (formatted.length === 0 && search && search.length >= 1) {
      setOptions([{ label: search, value: "newTag__" + search }]);
    } else {
      setOptions(formatted);
    }
  };

  const debouncedFetch = debounce(fetchTagSuggestions, 100);

  if (loading) {
    return (
      <Flex align="center" gap="middle" className="pageLoader">
        <Spin
          indicator={<LoadingOutlined spin />}
          className="m-auto"
          size="large"
        />
      </Flex>
    );
  }

  const ApprovalTimelineContent = () => {
    const approvalsData = originalData?.projectApprovals;
    if (!approvalsData || approvalsData?.length === 0)
      return <Badge color="outline-danger">No</Badge>;
    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status == "Approved";
              const isRejected = approval.status == "Rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}{" "}
                  {approval?.reason && (
                    <Icon
                      name="info"
                      className="ms-1"
                      title={`Reason : ${approval?.reason}`}
                      // style={{
                      //   cursor: "pointer",
                      // }}
                    />
                  )}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.approvalStage && (
                    <div className="designation">{approval?.approvalStage}</div>
                  )}
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2557d6",
            textDecoration: "underline",
            cursor: "pointer",
          }}
          // onClick={() => {
          //   setApprovals({
          //     open: true,
          //     data: approvalsData,
          //   });
          // }}
        >
          Yes
        </span>
      </Popover>
    );
  };

  return (
    <>
      {contextHolder}
      <OwnerChangeReason
        open={ownerChangeModalOpen?.open}
        setOpen={(open: boolean) => {
          setOwnerChangeModalOpen({
            open: open,
            extraData: { ...ownerChangeModalOpen?.extraData },
          });
        }}
        onSubmit={(data: any) => {
          setLoading(true);
          updateProject({
            ...ownerChangeModalOpen?.extraData,
            reassignedReason: data,
          });
        }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flex: 1, marginRight: "60px" }}>
            <Alert
              isOpen={originalData?.derivedProjectStatus == "Inactive"}
              color="danger"
              className="py-1 mt-2"
              style={{
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <span className="fw-bold">Inactive:</span>{" "}
              <ReactTyped
                strings={[
                  `This Project is currently inactive. Last updated on ${dayjs(
                    originalData?.updatedAt
                  ).format("DD-MMM-YYYY")} by "${
                    originalData?.updatedByUserName || "N/A"
                  }".`,
                ]}
                typeSpeed={5}
                showCursor={false}
              />
            </Alert>

            <Alert
              isOpen={
                originalData?.lock == true &&
                originalData?.derivedProjectStatus !== "Draft" &&
                originalData?.derivedProjectStatus !== "Pending Approval" &&
                originalData?.lockedBy != currentUser?.id
              }
              color="warning"
              className="py-1 mt-2"
              style={{
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <span className="fw-bold">Locked:</span>{" "}
              <ReactTyped
                strings={[
                  `This Project is currently Locked. Last updated on ${dayjs(
                    originalData?.updatedAt
                  ).format("DD-MMM-YYYY")} by "${
                    originalData?.lockedByUserName || "N/A"
                  }".`,
                ]}
                typeSpeed={5}
                showCursor={false}
              />
            </Alert>

            <Alert
              isOpen={warning?.message != undefined}
              color="danger"
              className="py-1 mt-2"
              style={{
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <span className="fw-bold">{warning?.error}: </span>
              <ReactTyped
                strings={[warning?.message || ""]}
                typeSpeed={10}
                showCursor={false}
              />
            </Alert>
          </div>
          <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
            {!isEditMode && (
              <>
                {(canEdit("Projects") ||
                  canCancel("Projects") ||
                  canApprove("Projects")) &&
                  (() => {
                    // Check if there are any menu items to show
                    const hasEditOption =
                      originalData?.lock == false ||
                      (originalData?.lock == true &&
                        originalData?.lockedBy == currentUserId &&
                        originalData?.derivedProjectStatus !== "Pending Approval");
                    const hasCloseProject =
                      originalData?.phasesCompletionPercentage === 100;
                    const hasRequestApproval =
                      (originalData?.approvalStatus == "Draft" ||
                        originalData?.approvalStatus == "Rejected") &&
                      originalData?.lockedBy == currentUserId;
                    const hasArchive = originalData?.derivedProjectStatus === "Closed";
                    const hasApproveReject =
                      originalData?.derivedProjectStatus === "Pending Approval" &&
                      originalData?.projectApprovals?.find(
                        (data: any) =>
                          data.userId === currentUserId && data.status !== "Approved"
                      );
                    const hasDeactivate =
                      originalData?.derivedProjectStatus == "Active" &&
                      originalData?.lock == false;
                    const hasActivate = originalData?.derivedProjectStatus === "Inactive";
                    const hasLock = originalData?.lock == false;
                    const hasUnlock =
                      originalData?.lock == true &&
                      originalData?.lockedBy == currentUserId &&
                      originalData?.derivedProjectStatus !== "Draft" &&
                      originalData?.derivedProjectStatus !== "Pending Approval";

                    const hasAnyMenuItem =
                      hasEditOption ||
                      hasCloseProject ||
                      hasRequestApproval ||
                      hasArchive ||
                      hasApproveReject ||
                      hasDeactivate ||
                      hasActivate ||
                      hasLock ||
                      hasUnlock;

                    return hasAnyMenuItem ? (
                      <li>
                        <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="dropdown-toggle btn btn-icon btn-trigger"
                      >
                        <Icon name="more-v"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end>
                        <ul className="link-list-opt">
                          {/* no-bdr */}
                          {(originalData?.lock == false ||
                            (originalData?.lock == true &&
                              originalData?.lockedBy == currentUserId &&
                              originalData?.derivedProjectStatus !==
                                "Pending Approval")) && (
                            <li>
                              <EditAccess moduleName="Projects">
                                <DropdownItem
                                  tag="a"
                                  href="#tableView"
                                  onClick={(ev: any) => {
                                    ev.preventDefault();
                                    setIsEditMode(true);
                                  }}
                                >
                                  <span>Edit </span>
                                </DropdownItem>
                              </EditAccess>
                            </li>
                          )}
                          {originalData?.phasesCompletionPercentage === 100 && (
                            <li>
                              {/* <CancelAccess moduleName="Projects"> */}
                              <DropdownItem
                                tag="a"
                                href="#detailView"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  closeProject();
                                }}
                              >
                                <span>Close Project</span>
                              </DropdownItem>
                              {/* </CancelAccess> */}
                            </li>
                          )}
                          {(originalData?.approvalStatus == "Draft" ||
                            originalData?.approvalStatus == "Rejected") &&
                            originalData?.lockedBy == currentUserId && (
                              <li>
                                <EditAccess moduleName="Projects">
                                  <DropdownItem
                                    tag="a"
                                    href="#detailView"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      requestForApproval();
                                    }}
                                  >
                                    <span>Request For Approvals</span>
                                  </DropdownItem>
                                </EditAccess>
                              </li>
                            )}
                          {originalData?.derivedProjectStatus === "Closed" && (
                            <li>
                              {/* <CancelAccess moduleName="Projects"> */}
                              <DropdownItem
                                tag="a"
                                href="#detailView"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  archiveProject();
                                }}
                              >
                                <span>Archive</span>
                              </DropdownItem>
                              {/* </CancelAccess> */}
                            </li>
                          )}
                          {originalData?.derivedProjectStatus ===
                            "Pending Approval" &&
                            originalData?.projectApprovals.find(
                              (data: any) =>
                                data.userId === currentUserId &&
                                data.status !== "Approved"
                            ) && (
                              <>
                                <li>
                                  <ApproveAccess moduleName="Projects">
                                    <DropdownItem
                                      tag="a"
                                      href="#cardView"
                                      onClick={(ev) => {
                                        ev.preventDefault();
                                        approveProject();
                                      }}
                                    >
                                      <span>Approve</span>
                                    </DropdownItem>
                                  </ApproveAccess>
                                </li>
                                <li>
                                  <RejectAccess moduleName="Projects">
                                    <DropdownItem
                                      tag="a"
                                      href="#cardView"
                                      onClick={(ev) => {
                                        ev.preventDefault();
                                        rejectProject();
                                      }}
                                    >
                                      <span>Reject</span>
                                    </DropdownItem>
                                  </RejectAccess>
                                </li>
                              </>
                            )}
                          <>
                            {originalData?.derivedProjectStatus == "Active" &&
                              originalData?.lock == false && (
                                <li>
                                  <CancelAccess moduleName="Projects">
                                    <DropdownItem
                                      tag="a"
                                      href="#cardView"
                                      onClick={(ev) => {
                                        ev.preventDefault();
                                        deactivateProject();
                                      }}
                                    >
                                      <span>Deactivate</span>
                                    </DropdownItem>
                                  </CancelAccess>
                                </li>
                              )}
                            {originalData?.derivedProjectStatus ===
                              "Inactive" && (
                              <li>
                                <EditAccess moduleName="Projects">
                                  <DropdownItem
                                    tag="a"
                                    href="#detailView"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      activateProject();
                                    }}
                                  >
                                    <span>Activate</span>
                                  </DropdownItem>
                                </EditAccess>
                              </li>
                            )}
                          </>
                          {/* )} */}
                          {originalData?.lock == false && (
                            <li>
                              <EditAccess moduleName="Projects">
                                <DropdownItem
                                  tag="a"
                                  href="#lockView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    lockProject();
                                  }}
                                >
                                  <span>Lock</span>
                                </DropdownItem>
                              </EditAccess>
                            </li>
                          )}
                          {originalData?.lock == true &&
                            originalData?.lockedBy == currentUserId &&
                            originalData?.derivedProjectStatus !== "Draft" &&
                            originalData?.derivedProjectStatus !==
                              "Pending Approval" && (
                              <li>
                                <EditAccess moduleName="Projects">
                                  <DropdownItem
                                    tag="a"
                                    href="#lockView"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      unLockProject();
                                    }}
                                  >
                                    <span>UnLock</span>
                                  </DropdownItem>
                                </EditAccess>
                              </li>
                            )}
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                      </li>
                    ) : null;
                  })()}
              </>
            )}
            {isEditMode && (
              <>
                <li>
                  <Button
                    className="btn btn-primary btn-sm mt-2"
                    type="submit"
                    color="primary"
                    htmlType="submit"
                  >
                    <Icon name={"save"} />
                    <span>Save</span>
                  </Button>
                </li>
                <li>
                  <Button
                    className="btn btn-secondary btn-sm mt-2"
                    type="button"
                    color="secondary"
                    onClick={() => {
                      Swal.fire({
                        title: "Are you sure?",
                        text: "You won't be able to revert this!",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#3085d6",
                        cancelButtonColor: "#d33",
                        confirmButtonText: "Yes, cancel it!",
                      }).then((result) => {
                        if (result.isConfirmed) {
                          setIsEditMode(false);
                          fetchDataById(id);
                          Swal.fire({
                            title: "Cancelled",
                            icon: "success",
                            text: "Your changes have been cancelled.",
                          });
                          // When the form was mounted directly into edit mode
                          // from the new ProjectQuickViewCard summary, bounce
                          // the user back to that summary instead of leaving
                          // them on the legacy in-form read-only view.
                          if (onBackToSummary) {
                            onBackToSummary();
                          }
                        }
                      });
                    }}
                  >
                    <span>Cancel</span>
                  </Button>
                </li>
              </>
            )}
          </ul>
        </div>
        <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix mt-2">
          <FormProvider {...formMethods}>
            <Row gutter={[24, 16]}>
              {/* Column 1 */}
              <Col xs={24} lg={8}>
                <Row gutter={[16, 16]}>
                  {/* Project Title */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Project Title
                          {isEditMode && <span className="mandatory m-1">*</span>}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormInput
                            name="projectTitle"
                            hideRequiredMark={true}
                            label={""}
                            disabled
                            required={true}
                            type="text"
                            placeholder="Enter Project Title"
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>{watch("projectTitle")}</div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Planned Start Date */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Planned Start Date
                          {isEditMode && <span className="mandatory m-1">*</span>}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormDatePicker
                            name="plannedStartDate"
                            hideRequiredMark={true}
                            label={""}
                            required={true}
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>
                            {watch("plannedStartDate")
                              ? dayjs(watch("plannedStartDate")).format("DD-MMM-YYYY")
                              : "-"}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Planned End Date */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Planned End Date
                          {isEditMode && <span className="mandatory m-1">*</span>}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormDatePicker
                            name="plannedEndDate"
                            hideRequiredMark={true}
                            label={""}
                            required={true}
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>
                            {watch("plannedEndDate")
                              ? dayjs(watch("plannedEndDate")).format("DD-MMM-YYYY")
                              : "-"}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Actual Start Date - view only */}
                  {!isEditMode && (
                    <Col span={24}>
                      <div className="d-flex align-items-start">
                        <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                          <strong>Actual Start Date</strong>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ paddingTop: "8px" }}>
                            {originalData?.actualStartDate
                              ? dayjs(originalData.actualStartDate).format("DD-MMM-YYYY")
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}

                  {/* Actual End Date - view only */}
                  {!isEditMode && (
                    <Col span={24}>
                      <div className="d-flex align-items-start">
                        <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                          <strong>Actual End Date</strong>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ paddingTop: "8px" }}>
                            {originalData?.actualEndDate
                              ? dayjs(originalData.actualEndDate).format("DD-MMM-YYYY")
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}

                  {/* Strict Project */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>Strict Project</strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormCheckbox
                            name="isStrict"
                            optionLabel="Make this a strict project"
                            noGap={true}
                            size={"small"}
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>
                            {watch("isStrict") === true ? "Yes" : "No"}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                </Row>
              </Col>

              {/* Column 2 */}
              <Col xs={24} lg={8}>
                <Row gutter={[16, 16]}>
                  {/* Site */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Site
                          {isEditMode && <span className="mandatory m-1">*</span>}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormSelect
                            name="siteId"
                            hideRequiredMark={true}
                            label={""}
                            options={sitesList}
                            required={true}
                            disabled
                            placeholder="-Select-"
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>{watch("siteName") || "-"}</div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Calendar */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Calendar
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormSelect
                            name="calendarId"
                            hideRequiredMark={true}
                            label={""}
                            options={calendarList}
                            required={false}
                            placeholder="-Select-"
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>{watch("calendarTitle") || "-"}</div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Rate Card */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Rate Card
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormSelect
                            name="rateCardId"
                            hideRequiredMark={true}
                            label={""}
                            options={ratecardsList}
                            required={false}
                            placeholder="-Select-"
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>{watch("projectRateCardName") || "-"}</div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Approval Required */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Approval Required
                          {isEditMode && <span className="mandatory m-1">*</span>}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormSelect
                            name="approvalRequired"
                            hideRequiredMark={true}
                            label={""}
                            disabled
                            options={[
                              { label: "Yes", value: "yes" },
                              { label: "No", value: "no" },
                            ]}
                            placeholder="Select"
                            required={true}
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>
                            <ApprovalTimelineContent />
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                </Row>
              </Col>

              {/* Column 3 */}
              <Col xs={24} lg={8}>
                <Row gutter={[16, 16]}>
                  {/* Project Manager */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Project Manager{" "}
                          {originalData?.originalProjectOwnerId &&
                          originalData?.originalProjectOwnerId != originalData?.ownerId ? (
                            <Popconfirm
                              title={<></>}
                              icon={<></>}
                              showCancel={false}
                              trigger="hover"
                              okButtonProps={{
                                style: {
                                  display: "none",
                                },
                              }}
                              description={
                                <Descriptions
                                  title=""
                                  bordered
                                  column={1}
                                  size="small"
                                  className="custom-description-title"
                                >
                                  <Descriptions.Item label="Original Owner">
                                    {originalData?.originalProjectOwnerName || "N/A"}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="Reason">
                                    {originalData?.reassignedReason || "N/A"}
                                  </Descriptions.Item>
                                </Descriptions>
                              }
                            >
                              <Icon
                                name="info"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "13px",
                                }}
                              />
                            </Popconfirm>
                          ) : null}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormSelect
                            name="projectOwnerId"
                            hideRequiredMark={true}
                            label={""}
                            options={ownersList}
                            required={true}
                            placeholder="Select Owner"
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>{watch("projectOwnerName") || "-"}</div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Budget */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Budget
                          {isEditMode && <span className="mandatory m-1">*</span>}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormSelect
                            name="budgetId"
                            hideRequiredMark={true}
                            label={""}
                            options={budgetList}
                            required={true}
                            placeholder="Select Budget"
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>{watch("budgetName") || "-"}</div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Group */}
                  <Col span={24}>
                    <div className="d-flex align-items-start">
                      <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                        <strong>
                          Group
                          {isEditMode && <span className="mandatory m-1">*</span>}
                        </strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        {isEditMode ? (
                          <FormSelect
                            name="projectGroupId"
                            hideRequiredMark={true}
                            label={""}
                            disabled
                            options={groupsList}
                            required={true}
                            placeholder="-Select-"
                          />
                        ) : (
                          <div style={{ paddingTop: "8px" }}>{watch("projectGroupName") || "-"}</div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Must Finish By */}
                  {watch("isStrict") === true && (
                    <Col span={24}>
                      <div className="d-flex align-items-start">
                        <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                          <strong>
                            Must Finish By
                            {isEditMode && <span className="mandatory m-1">*</span>}
                          </strong>
                        </div>
                        <div style={{ flex: 1 }}>
                          {isEditMode ? (
                            <FormDatePicker
                              name="mustFinishDate"
                              hideRequiredMark={true}
                              label={""}
                              required={false}
                              minDate={new Date()}
                            />
                          ) : (
                            <div style={{ paddingTop: "8px" }}>
                              {watch("mustFinishDate")
                                ? dayjs(watch("mustFinishDate")).format("DD-MMM-YYYY")
                                : "-"}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>

            {/* No-Approval Remarks (shown only when Approval Required = No) */}
            {watch("approvalRequired") === "no" && (
              <Row gutter={[24, 16]} className="mt-3">
                <Col span={24}>
                  <div className="d-flex align-items-start">
                    <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                      <strong>
                        No-Approval Remarks
                        {isEditMode && (
                          <span className="mandatory m-1">*</span>
                        )}
                      </strong>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#8c8c8c",
                          marginTop: "2px",
                          fontWeight: 400,
                        }}
                      >
                        Reason for skipping approvals
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      {isEditMode ? (
                        <FormInput
                          name="autoApprovalRemarks"
                          hideRequiredMark={true}
                          label={""}
                          type="textarea"
                          rows={2}
                          required={true}
                          minLength={2}
                          maxLength={500}
                          placeholder="Enter reason for not requiring approvals"
                        />
                      ) : (
                        <div
                          style={{
                            borderLeft: "3px solid #faad14",
                            background: "#fffbe6",
                            padding: "8px 10px",
                            borderRadius: "4px",
                            fontStyle: watch("autoApprovalRemarks")
                              ? "normal"
                              : "italic",
                            color: watch("autoApprovalRemarks")
                              ? "#262626"
                              : "#8c8c8c",
                          }}
                        >
                          {watch("autoApprovalRemarks") || "No remarks provided"}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {/* Project Resources Section */}
            <Row gutter={[24, 16]} className="mt-4">
              <Col span={24}>
                <strong style={{ fontSize: "15px" }}>
                  Project Resources
                  {!isEditMode && (
                    <span
                      style={{
                        fontWeight: 400,
                        color: "#8c8c8c",
                        fontSize: "12px",
                        marginLeft: "8px",
                      }}
                    >
                      ({(watch("users") || []).length} assigned)
                    </span>
                  )}
                </strong>
              </Col>
              <Col span={24}>
                {isEditMode ? (
                  resourceHeads?.length > 0 ? (
                    <Controller
                      name="users"
                      control={control}
                      defaultValue={[]}
                      render={({ field }) => (
                        <Checkbox.Group
                          {...field}
                          style={{ width: "100%" }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(220px, 1fr))",
                              gap: "8px 16px",
                              width: "100%",
                            }}
                          >
                            {resourceHeads?.map((option) => (
                              <Checkbox
                                key={option.value}
                                value={option.value}
                              >
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "inline-block",
                                    maxWidth: "180px",
                                    verticalAlign: "middle",
                                  }}
                                  title={option.label}
                                >
                                  {option.label}
                                </span>
                              </Checkbox>
                            ))}
                          </div>
                        </Checkbox.Group>
                      )}
                    />
                  ) : (
                    <div style={{ color: "red" }}>
                      No other resource heads available.
                    </div>
                  )
                ) : (watch("users") || []).length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {(watch("users") || []).map((userId: any) => {
                      const resource = resourceHeads?.find(
                        (r) => r.value === userId
                      );
                      const label =
                        resource?.label ||
                        ownersList?.find((o) => o.value === userId)?.label ||
                        `User ${userId}`;
                      return (
                        <Tag
                          key={userId}
                          style={{
                            padding: "4px 10px",
                            fontSize: "12px",
                            borderRadius: "12px",
                            background: "#e6f7ff",
                            border: "1px solid #91d5ff",
                            color: "#1890ff",
                          }}
                        >
                          {label}
                        </Tag>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ color: "#8c8c8c", fontStyle: "italic" }}>
                    No resources assigned.
                  </div>
                )}
              </Col>
            </Row>

            {/* Project Access Section */}
            <Row gutter={[24, 16]} className="mt-3">
              <Col span={24}>
                <strong style={{ fontSize: "15px" }}>Project Access</strong>
              </Col>
              <Col xs={24} lg={12}>
                <Controller
                  name="isPrivate"
                  disabled={!isEditMode}
                  control={control}
                  defaultValue={"true"}
                  render={({ field }) => (
                    <Radio.Group {...field} disabled={!isEditMode}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <Radio value={"true"}>
                          <strong>Private</strong>
                          <div style={{ fontSize: "12px" }}>
                            Only project users can view and access this project.
                          </div>
                        </Radio>
                        <Radio value={"false"}>
                          <strong>Public</strong>
                          <div style={{ fontSize: "12px" }}>
                            Portal users can only view, follow, and comment
                            whereas project users will have complete access.
                          </div>
                        </Radio>
                      </div>
                    </Radio.Group>
                  )}
                />
              </Col>
            </Row>

            {/* Material BOQ and Tags Section */}
            <Row gutter={[24, 16]} className="mt-4">
              {/* Material BOQ */}
              <Col xs={24} lg={12}>
                <div className="d-flex align-items-start">
                  <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                    <strong>Material BOQ</strong>
                  </div>
                  <div style={{ flex: 1 }}>
                    {isEditMode ? (
                      <FormMultiSelect
                        name="boqs"
                        label={undefined}
                        options={boqList}
                        required={false}
                        placeholder="Select Material BOQ"
                      />
                    ) : (
                      <div style={{ paddingTop: "8px" }}>
                        {(() => {
                          const selectedIds = watch("boqs") || [];
                          const originalBoqs =
                            originalData?.projectBoqs || [];
                          const names = selectedIds
                            .map((id: any) => {
                              const fromOriginal = originalBoqs.find(
                                (b: any) => b.boqTargetId === id
                              );
                              return (
                                fromOriginal?.boqTitle ||
                                boqList.find((b) => b.value === id)?.label ||
                                ""
                              );
                            })
                            .filter(Boolean)
                            .join(", ");
                          return names || "N/A";
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              {/* Tags */}
              <Col xs={24} lg={12}>
                <div className="d-flex align-items-start">
                  <div style={{ minWidth: "100px", paddingTop: "8px" }}>
                    <strong>Tags</strong>
                  </div>
                  <div style={{ flex: 1 }}>
                    {isEditMode ? (
                      <Select
                        mode="multiple"
                        style={{ width: "100%" }}
                        placeholder="Type and press enter to create or select a tag"
                        labelInValue
                        value={tags}
                        onSearch={debouncedFetch}
                        onDropdownVisibleChange={(open) => {
                          if (open && projectTagsList?.length > 0) {
                            fetchTagSuggestions("");
                          }
                        }}
                        onDeselect={(e: {
                          label: string;
                          value: string | number;
                        }) => {
                          if (typeof e?.value === "number") {
                            setDeselectedTags((pre) => {
                              return [...pre, e.value];
                            });
                          }
                        }}
                        onChange={(value) => {
                          const latest: any = value?.slice(-1)[0];
                          if (latest?.label?.length < 2) {
                            messageApi.open({
                              type: "error",
                              content: "Enter at least 2 characters",
                              duration: 5,
                            });
                            setOptions([]);
                            return;
                          }
                          setTags(value);
                        }}
                        options={options}
                        tokenSeparators={[","]}
                        tagRender={tagRender}
                        filterOption={false}
                      />
                    ) : (
                      <div style={{ paddingTop: "8px" }}>
                        {tags && tags.length > 0 ? (
                          tags.map((tag: any, index: number) => {
                            const color = getColorByLabel(
                              (tag.label || "").toString()
                            );
                            return (
                              <span key={index} className="custom-tag-wrapper">
                                <Tag
                                  className="custom-tag"
                                  style={{ backgroundColor: color }}
                                >
                                  {tag.label}
                                </Tag>
                                <span
                                  className="custom-tag-tail"
                                  style={{ borderLeftColor: color }}
                                />
                              </span>
                            );
                          })
                        ) : (
                          <span>N/A</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Description Section */}
            <Row gutter={[24, 16]} className="mt-3">
              <Col span={24}>
                <div className="d-flex align-items-start">
                  <div style={{ minWidth: "140px", paddingTop: "8px" }}>
                    <strong>
                      Description
                      {isEditMode && <span className="mandatory m-1">*</span>}
                    </strong>
                  </div>
                  <div style={{ flex: 1 }}>
                    {isEditMode ? (
                      <FormInput
                        name="projectDescription"
                        label=""
                        hideRequiredMark={true}
                        required={true}
                        type="text"
                        minLength={2}
                        maxLength={200}
                        placeholder="Enter Description"
                      />
                    ) : (
                      <div style={{ paddingTop: "8px" }}>
                        {watch("projectDescription") || "-"}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </FormProvider>
        </div>
      </form>
    </>
  );
};

export default ProjectInformation;
